import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import "./App.css";
import AppShell from "./components/shell/AppShell";
import { ASKMILA_PATH } from "./components/shell/agentRegistry";
import { AgentAccessProvider } from "./components/context/AgentAccessContext";
import { FeatureFlagsProvider } from "./components/context/FeatureFlagsContext";
import apiClient from "./business/network/ApiClient";
import LoginPage from "./components/specialized/LoginPage";
import MilaContext from "./components/context/MilaContext";
import { OnboardingProvider } from "./components/context/OnboardingContext";
import Chip from "./components/specialized/Chip";
import utils from "./business/helper/Utils";

/**
 * Whether this browser is signed in — three answers, not two.
 *
 * "Checking" is the one that was missing, and its absence is what ate every address the user
 * arrived on. A reload starts with the token sitting in `localStorage` and the flag still
 * false, so the guard below sent them to `/login` — and the address they had just reloaded was
 * gone a frame later, long before anyone could sign in and be sent back to it. Nothing
 * navigates while the answer is "checking", so the address survives the wait.
 */
const AUTH = {
  Checking: "checking",
  Anonymous: "anonymous",
  SignedIn: "signedIn",
};

/** `localStorage` throws outright in some embedded contexts rather than returning null. */
const hasStoredToken = () => {
  try {
    return Boolean(window.localStorage.getItem("Token"));
  } catch {
    return false;
  }
};

/**
 * What is on screen while the session is being worked out. A bare ground rather than a spinner:
 * it is a second at most, and the app's own background is a quieter wait than something that
 * appears only to be replaced.
 */
const BootScreen = () => <div className="h-screen w-full bg-whitesmoke" />;

const ProtectedRoute = ({ status, children }) => {
  const location = useLocation();

  if (status === AUTH.Checking) return <BootScreen />;

  if (status === AUTH.Anonymous) {
    return (
      <Navigate
        to="/login"
        replace
        // Where they were going, carried through the sign-in so it finishes the journey
        // rather than landing everybody on the chat.
        state={{ from: `${location.pathname}${location.search}${location.hash}` }}
      />
    );
  }

  return children;
};

/** Back to whatever the sign-in interrupted, or to the front door if it interrupted nothing. */
const ReturnFromLogin = () => {
  const location = useLocation();
  return <Navigate to={location.state?.from || ASKMILA_PATH} replace />;
};

export const centralUrl = `${process.env.REACT_APP_CURRENT_CENTRAL_HOST}`;
export const envContext = window.__MILA_ENV__;

function App() {
  const [authStatus, setAuthStatus] = useState(() =>
    hasStoredToken() ? AUTH.Checking : AUTH.Anonymous
  );

  const [isCentralOrigin, setIsCentralOrigin] = useState(false);

  const milaContextValue = {
    triggers: {
      displayFiles: (threadId, index, historyUploadedFiles) => {
        if (milaContextValue.uploadedFiles && milaContextValue.uploadedFiles.length > 0) {
          //override by history threadId if available
          if (historyUploadedFiles && historyUploadedFiles.length > 0) {
            threadId = historyUploadedFiles[0].thread_id;
          }

          const files = milaContextValue.uploadedFiles.filter(file => file.thread_id === threadId && file.question_index === index);
          if (files && files.length > 0) {
            return (
              <>
                {files.map(file => (
                  <>
                    <Chip
                      key={file.file_id}
                      isDisplayMode={true}
                      label={
                        file.file_name?.length > 30
                          ? file.file_name.substring(0, 29) + '...'
                          : file.file_name
                      }
                      title={file.file_name}
                      variant="outlined"
                      icon={utils.getFileIcon(file.file_extension)}
                      className={''}
                    />
                    <>&nbsp;</>
                  </>
                ))}
              </>
            );
          }
        }
        return <></>;
      }
    },
    uploadedFiles: [],
    language: navigator.language || navigator.userLanguage || "en-GB",
  };

  useEffect(() => {
    let timer;

    if (hasStoredToken()) {
      timer = setTimeout(() => {
        setAuthStatus(AUTH.SignedIn)
      }, 1500);
    }

    window.opener?.postMessage("ready", centralUrl);

    const handleMessage = (event) => {
      if (event.origin !== centralUrl) {
        return;
      }

      localStorage.removeItem("Token");
      const userObject = event.data;
      if (userObject && userObject?.userData?.userId && userObject?.token) {
        console.log("Received userobject : ", userObject);
        let tokenObj = {};
        tokenObj.token = userObject?.token;
        tokenObj.userData = userObject?.userData;

        const process = async () => {
          localStorage.setItem("Token", JSON.stringify(tokenObj));
          console.log("Token set in local storage");
          var milaToken = {};
          milaToken.accessToken = userObject?.token?.access_token;
          milaToken.refreshToken = userObject?.token?.refresh_token;
          milaToken.ouId = Number(userObject?.userData?.ouId);

          await apiClient.setMilaToken(milaToken);
          setIsCentralOrigin(true);
          handleLogin();
        };
        process();
      }
    };

    window.addEventListener("message", handleMessage);

    return () => {
      clearTimeout(timer);
      window.removeEventListener("message", handleMessage);
    };
  }, []);

  const handleLogin = () => {
    setAuthStatus(AUTH.SignedIn);
  };

  const handleLogout = () => {
    if (isCentralOrigin) {
      window.open(centralUrl);
      return;
    }

    localStorage.removeItem("Token");
    setAuthStatus(AUTH.Anonymous);
    apiClient.isAuthenticated = false;
  };

  return (
    <MilaContext.Provider value={milaContextValue}>
      <BrowserRouter>
        <Routes>
          <Route
            path="/login"
            element={
              authStatus === AUTH.SignedIn ? (
                <ReturnFromLogin />
              ) : (
                envContext === "production" ? (<></>) : (<LoginPage onLogin={handleLogin} />)
              )
            }
          />

          {/* Every AI service, through one route.
              `/mila` is AskMila and each agent has an address of its own
              (`/incident-prevention-agent`); which one is on screen is read from the address by
              the shell, which also puts right anything the address bar got wrong (AppShell).

              One route rather than one per service, and not by accident: two routes rendering
              the same element are two mounts as far as React is concerned, so stepping from the
              chat into an agent and back would tear AskMila down — SignalR connection, streamed
              answer and all — every time. A single route keeps the shell, and everything inside
              it, exactly where it was. */}
          <Route
            path="/:view"
            element={
              <ProtectedRoute status={authStatus}>
                <OnboardingProvider>
                  {/* The shell adds the left rail around the existing AskMila screen. When the
                      user is entitled to no agents the rail collapses to AskMila alone, so this
                      is a no-op for them (design §13.1). */}
                  <FeatureFlagsProvider>
                    <AgentAccessProvider>
                      <AppShell
                        handleLogout={handleLogout}
                        isFromCentral={isCentralOrigin}
                      />
                    </AgentAccessProvider>
                  </FeatureFlagsProvider>
                </OnboardingProvider>
              </ProtectedRoute>
            }
          />

          {/* `/`, and anything deeper than the one segment a service is addressed by. Sent to
              the front door rather than to the login page: whether a sign-in is needed first is
              the guard's business, not this route's, and it is the only one that can tell. */}
          <Route path="*" element={<Navigate to={ASKMILA_PATH} replace />} />
        </Routes>
      </BrowserRouter>
    </MilaContext.Provider>
  );
}

export default App;
